import { useState } from 'react';
import Reveal from '@/components/base/Reveal';
import SectionHeading from '@/components/base/SectionHeading';
import { castNote, castTabs, characters } from '@/mocks/drama';

const tierLabel: Record<string, string> = {
  lead: '核心主角',
  support: '关键人物',
  antagonist: '反面势力',
};

const noteByTier: Record<string, string> = {
  lead: '跨单元核心出演',
  support: '单元关键角色出演',
  antagonist: '对抗线主要出演',
};

export default function Cast() {
  const [active, setActive] = useState<string>('all');

  const rows = active === 'all' ? characters : characters.filter((item) => item.tier === active);

  return (
    <section id="cast" className="scroll-mt-32 bg-background-50 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <Reveal>
          <SectionHeading
            eyebrow="演员表 · 全部角色"
            title="按角色整理的演员表"
            desc="点击标签可以快速筛选核心主角、关键人物与反面势力，查看每个角色的定位与所属故事线。"
          />
        </Reveal>

        <Reveal delay={80}>
          <div className="mt-8 inline-flex flex-wrap gap-1 rounded-full border border-background-200 bg-background-100 p-1">
            {castTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActive(tab.key)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  active === tab.key
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:bg-background-200 hover:text-foreground-950'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="mt-6 overflow-hidden rounded-lg border border-background-200">
            <div className="hidden grid-cols-12 gap-4 bg-background-100 px-5 py-3 text-xs font-medium tracking-wide text-foreground-500 md:grid">
              <div className="col-span-3">角色</div>
              <div className="col-span-3">剧中定位</div>
              <div className="col-span-3">所属故事线</div>
              <div className="col-span-3">出演说明</div>
            </div>

            {rows.map((item, index) => (
              <div
                key={item.id}
                className={`grid grid-cols-1 gap-2 px-5 py-4 md:grid-cols-12 md:items-center md:gap-4 ${
                  index % 2 === 1 ? 'bg-background-100/60' : 'bg-background-50'
                } ${index !== 0 ? 'border-t border-background-200' : ''}`}
              >
                <div className="col-span-3 flex items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 font-heading text-sm font-bold text-primary-700">
                    {item.name.slice(0, 1)}
                  </span>
                  <div>
                    <div className="font-heading text-base font-semibold text-foreground-950">{item.name}</div>
                    <div className="text-xs text-foreground-500">{item.role}</div>
                  </div>
                </div>

                <div className="col-span-3 text-sm text-foreground-700">
                  <span className="mr-2 text-xs text-foreground-400 md:hidden">剧中定位</span>
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-800">
                    <i className="ri-user-3-line" />
                    {tierLabel[item.tier]}
                  </span>
                </div>

                <div className="col-span-3 text-sm text-foreground-700">
                  <span className="mr-2 text-xs text-foreground-400 md:hidden">所属故事线</span>
                  {item.storyline}
                </div>

                <div className="col-span-3 text-sm text-foreground-700">
                  <span className="mr-2 text-xs text-foreground-400 md:hidden">出演说明</span>
                  {noteByTier[item.tier]}
                  <span className="ml-2 rounded-md bg-background-200 px-2 py-0.5 text-[11px] text-foreground-500">
                    栏目剧演员
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={160}>
          <p className="mt-6 rounded-lg border border-background-200 bg-background-100/70 p-4 text-xs leading-relaxed text-foreground-600 md:text-sm">
            <i className="ri-error-warning-line mr-1 text-accent-600" />
            {castNote}
          </p>
        </Reveal>
      </div>
    </section>
  );
}